import Button from 'app/components/Button'
import FloatingSave from 'app/components/FloatingSave'
import FormInput from 'app/components/form/FormInput'
import FormPanel from 'app/components/form/FormPanel'
import FormSelect from 'app/components/form/FormComboSelect'
import OfficeHoursEditor from 'app/components/form/OfficeHoursEditor'
import { SmartForm } from 'app/components/form/SmartForm'
import WorkspaceSelector from 'app/components/WorkspaceSelector'
import { WorkspaceJson } from 'app/types/entities'
import type { UseLocationSettingsGeneralReturn } from './useLocationSettingsGeneral'

const timezones = [``, `US/Alaska`, `US/Central`, `US/Eastern`, `US/Hawaii`, `US/Mountain`, `US/Pacific`]

export type LocationSettingsGeneralComponentProps = UseLocationSettingsGeneralReturn & {
  createValidator: any
  editValidator: any
}

export function LocationSettingsGeneralComponent(props: LocationSettingsGeneralComponentProps) {
  const {
    user,
    locationId,
    isNew,
    states,
    location,
    defaultValues,
    workspace,
    isDirtyForm,
    openHours,
    formRef,
    canChangeLocationWorkspace,
    setOpenHours,
    setIsDirtyForm,
    onDelete,
    onChangeWorkspace,
    onSubmit,
    createValidator,
    editValidator,
  } = props

  if (location?.timezone && !timezones.includes(location.timezone)) {
    timezones.push(location.timezone)
  }

  if (!user) return null

  const hiddenInputs = [
    { name: `id`, value: location?.id },
    { name: `orgId`, value: location?.orgId ?? user.orgId },
    { name: `workspaceId`, value: workspace?.id || user.workspaceId },
  ]

  return (
    <SmartForm
      key={`${locationId ?? `new`}-${location?.version ?? `loading`}`}
      id={`location-settings-${locationId}`}
      className="!mt-0"
      method="post"
      validator={isNew ? createValidator : editValidator}
      defaultValues={defaultValues}
      hiddenInputs={isNew ? hiddenInputs.filter((hidden) => hidden.name !== `id`) : hiddenInputs}
      subaction={isNew ? `create` : `edit`}
      showDelete={!isNew}
      hideButtons={true}
      formRef={formRef}
      onDelete={onDelete}
      onSubmit={onSubmit}
      replace
    >
      <FormPanel title="Location Details" cols={6}>
        <div className="flex flex-col gap-y-6">
          <FormInput name="name" label="Location Name" cols={6} labelClassName="text-ui-sm" />
          <FormSelect
            name="type"
            label="Location Type"
            cols={6}
            options={[
              { label: `General`, value: `General` },
              { label: `Specialty`, value: `Specialty` },
            ]}
            defaultValue={location?.type}
          />
          <div className="flex gap-2.5">
            <div className="flex-1">
              <FormInput name="address" label="Address" cols={6} labelClassName="text-ui-sm" />
            </div>
            <div className="flex-1">
              <FormInput name="city" label="City" cols={6} labelClassName="text-ui-sm" />
            </div>
          </div>
          <div className="flex gap-2.5">
            <div className="flex-1">
              <FormSelect name="state" label="State" cols={6} options={states} defaultValue={location?.state} />
            </div>
            <div className="flex-1">
              <FormSelect
                name="timezone"
                label="Timezone"
                cols={6}
                options={timezones?.map((tz) => ({ label: tz, value: tz }))}
                defaultValue={location?.timezone}
              />
            </div>
          </div>
        </div>
      </FormPanel>

      <FormPanel title="Office Hours" cols={6}>
        <OfficeHoursEditor
          value={openHours}
          onChange={(hours) => {
            setOpenHours(hours)
            setIsDirtyForm(true)
          }}
          name="openHours"
          variant="inline"
          compact={false}
        />
      </FormPanel>

      {!isNew && canChangeLocationWorkspace && (
        <FormPanel title="Workspace" cols={6}>
          <WorkspaceSelector workspace={workspace} onChange={onChangeWorkspace} />
        </FormPanel>
      )}

      <div className="col-span-6 my-4 bg-surface pb-40">
        <div className="flex gap-4">
          {!isNew && user.type === `super` && (
            <Button label="Delete" size="md" mode="danger" className="justify-self-start" onClick={() => onDelete()} />
          )}
          <div className="grow" />
        </div>
      </div>
      <FloatingSave isDirtyForm={isDirtyForm} />
    </SmartForm>
  )
}
